import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import EngineerLayout from './EngineerLayout';
import { db } from '../../firebase';
import { ref as dbRef, onValue, update } from 'firebase/database';
import { toast } from 'react-toastify';
import { 
  FaArrowLeft,
  FaPlus,
  FaSort,
  FaEdit
} from 'react-icons/fa';

const EngineerApprovedWorks = () => {
    const navigate = useNavigate();
    const [works, setWorks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedWorks, setSelectedWorks] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterSharedWork, setFilterSharedWork] = useState('all');
    const [filterTaluka, setFilterTaluka] = useState('all');
    const [transferStatus, setTransferStatus] = useState('');

    const talukaOptions = [
        "मिरज",
        "तासगाव",
        "कवठेमहांकाळ",
        "वाळवा",
        "शिराळा",
        "खानापूर",
        "आटपाडी",
        "जत",
        "कडेगाव",
        "पलूस"
    ];

    // Fetch works (adjust paths as needed)
    useEffect(() => {
        const worksRef = dbRef(db, 'ekamwatapzp/WorkNames');
        const unsubscribe = onValue(worksRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                let flattenedWorks = [];
                
                Object.entries(data).forEach(([key, val]) => {
                    if (val && typeof val === 'object') {
                        // Check if it's a direct work object (has schemeName)
                        if (val.schemeName) {
                            if (val.status === 'Approved' || val.status === 'lottery_completed') {
                                flattenedWorks.push({
                                    id: key,
                                    dbPath: `ekamwatapzp/WorkNames/${key}`,
                                    meetingNumber: val.meetingNumber || val.category || 'General',
                                    ...val
                                });
                            }
                        } else {
                            // It's a category/meeting folder
                            const meetingNum = key;
                            Object.entries(val).forEach(([id, value]) => {
                                if (value && typeof value === 'object' && (value.status === 'Approved' || value.status === 'lottery_completed')) {
                                    flattenedWorks.push({
                                        id,
                                        dbPath: `ekamwatapzp/WorkNames/${meetingNum}/${id}`,
                                        meetingNumber: meetingNum,
                                        ...value
                                    });
                                }
                            });
                        }
                    }
                });
                
                setWorks(flattenedWorks.reverse()); 
            } else {
                setWorks([]);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedWorks(works.map(w => w.id));
        } else {
            setSelectedWorks([]);
        }
    };

    const handleSelectWork = (id) => {
        if (selectedWorks.includes(id)) {
            setSelectedWorks(selectedWorks.filter(item => item !== id));
        } else {
            setSelectedWorks([...selectedWorks, id]);
        }
    };


    const handleEditSharedWork = (work) => {
        setSelectedWorks([work.id]);
        setTransferStatus(work.sharedwork || '');
        // Scroll to top to see the transfer form
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleTransfer = async () => {
        if (selectedWorks.length === 0) {
            toast.warning("कृपया कमीत कमी एक काम निवडा (Please select at least one work)");
            return;
        }
        if (!transferStatus) {
            toast.warning("कृपया एक पर्याय निवडा (Please select an option)");
            return;
        }

        try {
            const updates = {};
            selectedWorks.forEach(workId => {
                const work = works.find(w => w.id === workId);
                if (work && work.dbPath) {
                    // Update the 'sharedwork' node with the selected dropdown value
                    updates[`${work.dbPath}/sharedwork`] = transferStatus;
                }
            });

            await update(dbRef(db), updates);
            toast.success("यशस्वीरीत्या हस्तांतरित झाले! (Transferred successfully!)");
            setSelectedWorks([]);
            setTransferStatus('');
        } catch (error) {
            toast.error("त्रुटी: " + error.message);
        }
    };

    // Filtered data based on search and sharedwork filter
    const filteredWorks = works.filter(work => {
        const matchesSearch = (
            (work.workName?.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (work.taluka?.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (work.id?.toLowerCase().includes(searchTerm.toLowerCase()))
        );

        let matchesSharedFilter = true;
        if (filterSharedWork === 'not_shared') {
            matchesSharedFilter = !work.sharedwork;
        } else if (filterSharedWork !== 'all') {
            matchesSharedFilter = work.sharedwork === filterSharedWork;
        }

        let matchesTalukaFilter = true;
        if (filterTaluka !== 'all') {
            matchesTalukaFilter = work.taluka === filterTaluka;
        }

        return matchesSearch && matchesSharedFilter && matchesTalukaFilter;
    });

    return (
        <EngineerLayout>
            <div className="p-8 bg-[#f4f7fe] min-h-screen">
                {/* Header Section */}
                <div className="flex justify-between items-center mb-8 px-2">
                    <div className="flex items-center gap-4">
                        <button 
                            onClick={() => navigate('/engineer/tender-works-menu')}
                            className="p-2 bg-white rounded-full shadow-sm hover:bg-gray-50 transition text-gray-600 border border-gray-100"
                        >
                            <FaArrowLeft size={16} />
                        </button>
                        <h1 className="text-xl font-bold text-gray-800 font-marathi">मंजूर कामाची यादी पहा</h1>
                    </div>
                    <div className="text-xs font-medium text-gray-400 font-marathi">
                        जिल्हा परिषद / <span className="text-gray-500 font-bold">मंजूर कामाची यादी पहा</span>
                    </div>
                </div>

                {/* Transfer Form at Top */}
                <div className="bg-white rounded shadow-md border border-gray-100 p-6 mb-8">
                    <div className="flex flex-col md:flex-row items-end gap-6">
                        <div className="w-full md:w-64">
                            <label className="block text-xs font-bold text-red-600 uppercase tracking-tighter italic font-marathi mb-2">
                                * वर हस्तांतरित करा
                            </label>
                            <div className="relative">
                                <select 
                                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 outline-none focus:border-blue-500 text-sm bg-gray-50/50 text-gray-500 appearance-none cursor-pointer font-marathi"
                                    value={transferStatus}
                                    onChange={(e) => setTransferStatus(e.target.value)}
                                >
                                    <option value="">स्थिती निवडा</option>
                                    <option value="सुबे">सुबे</option>
                                    <option value="मजूर">मजूर </option>
                                    <option value="खुला">खुला </option>
                                    <option value="ग्रामपंचायत">ग्रामपंचायत</option>
                                </select>
                                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                                </div>
                            </div>
                        </div>
                        <button 
                            onClick={handleTransfer}
                            className="bg-[#4e73df] hover:bg-[#2e59d9] text-white px-8 py-2.5 rounded-lg text-sm font-bold shadow-md shadow-blue-100 transition-all font-marathi active:scale-95"
                        >
                            ट्रान्सफर
                        </button>
                    </div>
                </div>

                {/* Main Card */}
                <div className="bg-white rounded shadow-md border border-gray-100 p-6 overflow-hidden">
                    {/* Toolbar */}
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
                        <div className="flex rounded overflow-hidden">
                            <button className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 text-xs font-bold transition font-marathi">कॉपी करा</button>
                            <button className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 text-xs font-bold border-l border-gray-500 transition font-marathi">एक्सेल</button>
                            <button className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 text-xs font-bold border-l border-gray-500 transition font-marathi">प्रिंट</button>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <label className="text-sm font-bold text-gray-600 font-marathi">तालुका:</label>
                                <select 
                                    className="border border-gray-200 rounded px-2 py-1.5 text-sm outline-none focus:border-blue-500 transition-all font-marathi bg-gray-50/30"
                                    value={filterTaluka}
                                    onChange={(e) => setFilterTaluka(e.target.value)}
                                >
                                    <option value="all">सर्व (All)</option>
                                    {talukaOptions.map((taluka, i) => (
                                        <option key={i} value={taluka}>{taluka}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex items-center gap-2">
                                <label className="text-sm font-bold text-gray-600 font-marathi">शेअर:</label>
                                <select 
                                    className="border border-gray-200 rounded px-2 py-1.5 text-sm outline-none focus:border-blue-500 transition-all font-marathi bg-gray-50/30"
                                    value={filterSharedWork}
                                    onChange={(e) => setFilterSharedWork(e.target.value)}
                                >
                                    <option value="all">सर्व (All)</option>
                                    <option value="सुबे">सुबे</option>
                                    <option value="मजूर">मजूर</option>
                                    <option value="खुला">खुला</option>
                                    <option value="ग्रामपंचायत">ग्रामपंचायत</option>
                                    <option value="not_shared">हस्तांतरित न केलेले</option>
                                </select>
                            </div>
                            <div className="flex items-center gap-3">
                                <label className="text-sm font-bold text-gray-600 font-marathi">शोध:</label>
                                <input 
                                    type="text" 
                                    className="border border-gray-200 rounded px-3 py-1.5 text-sm outline-none focus:border-blue-500 transition-all font-medium bg-gray-50/30"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto border border-gray-100 rounded mb-6 custom-scrollbar">
                        <table className="w-full border-collapse min-w-[1200px]">
                            <thead>
                                <tr className="border-b border-gray-200 bg-white">
                                    <th className="px-3 py-4 text-[11px] font-black text-gray-800 text-left border-r border-gray-100 w-12 group cursor-pointer hover:bg-gray-50 uppercase">
                                        <div className="flex items-center justify-between"># <FaSort className="text-gray-300 opacity-0 group-hover:opacity-100 transition" /></div>
                                    </th>
                                    <th className="px-3 py-4 text-[11px] font-black text-gray-800 text-center border-r border-gray-100 w-16 group cursor-pointer hover:bg-gray-50">
                                        <div className="flex items-center justify-center gap-2">
                                            <input 
                                                type="checkbox" 
                                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                onChange={handleSelectAll}
                                                checked={filteredWorks.length > 0 && selectedWorks.length === filteredWorks.length}
                                            />
                                            <FaSort className="text-gray-300 opacity-0 group-hover:opacity-100 transition" />
                                        </div>
                                    </th>
                                    <th className="px-3 py-4 text-[11px] font-black text-gray-800 text-left border-r border-gray-100 group cursor-pointer hover:bg-gray-50 uppercase">
                                        <div className="flex items-center justify-between">आयडी <FaSort className="text-gray-300 opacity-0 group-hover:opacity-100 transition" /></div>
                                    </th>
                                    <th className="px-3 py-4 text-[11px] font-black text-gray-800 text-left border-r border-gray-100 group cursor-pointer hover:bg-gray-50 font-marathi">
                                        <div className="flex items-center justify-between">तालुका <FaSort className="text-gray-300 opacity-0 group-hover:opacity-100 transition" /></div>
                                    </th>
                                    <th className="px-3 py-4 text-[11px] font-black text-gray-800 text-left border-r border-gray-100 group cursor-pointer hover:bg-gray-50 font-marathi">
                                        <div className="flex items-center justify-between">योजनेचे नाव <FaSort className="text-gray-300 opacity-0 group-hover:opacity-100 transition" /></div>
                                    </th>
                                    <th className="px-3 py-4 text-[11px] font-black text-gray-800 text-left border-r border-gray-100 group cursor-pointer hover:bg-gray-50 font-marathi">
                                        <div className="flex items-center justify-between">निविदा रक्कम <FaSort className="text-gray-300 opacity-0 group-hover:opacity-100 transition" /></div>
                                    </th>
                                    <th className="px-3 py-4 text-[11px] font-black text-gray-800 text-left border-r border-gray-100 min-w-[300px] group cursor-pointer hover:bg-gray-50 font-marathi">
                                        <div className="flex items-center justify-between">कामाचे नाव <FaSort className="text-gray-300 opacity-0 group-hover:opacity-100 transition" /></div>
                                    </th>
                                    <th className="px-3 py-4 text-[11px] font-black text-gray-800 text-left group cursor-pointer hover:bg-gray-50 font-marathi">
                                        <div className="flex items-center justify-between">स्थिती <FaSort className="text-gray-300 opacity-0 group-hover:opacity-100 transition" /></div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="9" className="px-3 py-12 text-center text-gray-400">
                                            <div className="w-8 h-8 border-4 border-blue-600/20 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                                        </td>
                                    </tr>
                                ) : filteredWorks.length === 0 ? (
                                    <tr className="bg-gray-50/50">
                                        <td colSpan="9" className="px-3 py-6 text-center text-gray-700 font-bold font-marathi text-sm italic">
                                            सारणीमध्ये कोणताही डेटा उपलब्ध नाही.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredWorks.map((work, index) => (
                                        <tr key={work.id} className="hover:bg-blue-50/20 group transition-colors border-b border-gray-50 last:border-0 text-xs text-gray-600 uppercase">
                                            <td className="px-3 py-4 border-r border-gray-100 font-bold">{index + 1}</td>
                                             <td className="px-3 py-4 border-r border-gray-100 text-center">
                                                {work.sharedwork && !selectedWorks.includes(work.id) ? (
                                                    <button 
                                                        onClick={() => handleEditSharedWork(work)}
                                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-blue-100 shadow-sm"
                                                        title="स्थिती बदलण्यासाठी क्लिक करा"
                                                    >
                                                        <FaEdit size={16} />
                                                    </button>
                                                ) : (
                                                    <input 
                                                        type="checkbox" 
                                                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                                        checked={selectedWorks.includes(work.id)}
                                                        onChange={() => handleSelectWork(work.id)}
                                                    />
                                                )}
                                            </td>
                                            <td className="px-3 py-4 border-r border-gray-100 font-bold">{work.id}</td>
                                            <td className="px-3 py-4 border-r border-gray-100 font-marathi">{work.taluka || "-"}</td>
                                            <td className="px-3 py-4 border-r border-gray-100 font-marathi">{work.schemeName || "-"}</td>
                                            <td className="px-3 py-4 border-r border-gray-100">{work.tenderAmount || "0"}</td>
                                            <td className="px-3 py-4 border-r border-gray-100 font-marathi italic leading-relaxed text-[11px]">{work.workName || "-"}</td>
                                            <td className="px-3 py-4 border-r border-gray-100 text-center font-bold flex flex-col gap-1 items-center">
                                                <span className={`px-2 py-1 rounded-full text-[9px] uppercase tracking-wider ${
                                                    work.status === 'lottery_completed' || work.status === 'Approved'
                                                    ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                                                    : 'bg-blue-100 text-blue-700 border border-blue-200'
                                                }`}>
                                                    {work.status === 'lottery_completed' ? 'Lottery Done' : (work.status === 'Approved' ? 'मंजूर' : (work.status || "-"))}
                                                </span>
                                                {work.sharedwork && (
                                                    <span className="text-[10px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded border border-gray-200">
                                                        सामायिक: {work.sharedwork}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Footer Section */}
                    <div className="flex flex-col lg:flex-row justify-between items-start gap-8">
                        <div>
                            <p className="text-sm font-bold text-gray-700 font-marathi mb-6">
                                {filteredWorks.length} पैकी 0 ते {filteredWorks.length} नोंदी दाखवत आहे
                            </p>
                        </div>

                        {/* Pagination */}
                        <div className="flex rounded overflow-hidden border border-gray-300 self-end lg:self-start">
                            <button className="bg-white border-r border-gray-200 px-6 py-2.5 text-sm font-bold text-gray-400 hover:bg-gray-50 transition font-marathi">मागील</button>
                            <button className="bg-white px-6 py-2.5 text-sm font-bold text-gray-400 hover:bg-gray-50 transition font-marathi">पुढे</button>
                        </div>
                    </div>
                </div>

                {/* Horizontal Scrollbar indicator matching image */}
                <div className="bg-gray-100 h-10 rounded shadow-inner border border-gray-200 flex items-center px-4 mt-8">
                    <div className="flex-grow bg-gray-300 h-2 rounded-full relative">
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full border-[6px] border-transparent border-r-gray-500 mr-2 cursor-pointer hover:border-r-gray-700 transition"></div>
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full border-[6px] border-transparent border-l-gray-500 ml-2 cursor-pointer hover:border-l-gray-700 transition"></div>
                    </div>
                </div>
            </div>

            <style>{`
                .custom-scrollbar::-webkit-scrollbar {
                    height: 0px;
                }
                .font-marathi {
                    font-family: 'Hind', sans-serif;
                }
                @import url('https://fonts.googleapis.com/css2?family=Hind:wght@400;500;600;700&display=swap');
            `}</style>
        </EngineerLayout>
    );
};

export default EngineerApprovedWorks;
